<template>
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-4">
    <div class="rounded border border-gray-300 bg-white px-4 py-5 lg:w-full">
      <h3
        class="font-semibold uppercase leading-8 tracking-normal text-gray-700 md:leading-3"
      >
        <span class="text-sm"> Total GHG Emissions </span>
      </h3>
      <div class="mb-2 mt-2 h-0.5 w-full bg-slate-300" />
      <div class="mt-3 flex items-center no-localize-translate">
        <h4
          class="font-medium text-slate-900"
          :class="{ 'text-2xl': isTextOverflow, 'text-3xl': !isTextOverflow }"
        >
          {{ currentYearTotalEmissions }}
        </h4>
        <span class="ml-2 text-sm text-gray-400">tCO2e</span>
      </div>
      <div class="mt-2 whitespace-nowrap text-sm font-medium text-gray-600">
        <span
          :class="[
            previousYearIncrease === 0
              ? 'text-gray-400'
              : previousYearIncrease > 0
                ? 'text-red-500'
                : 'text-green-500',
          ]"
        >
          {{ getPercentageDisplay(previousYearIncrease) }}
        </span>
        from the previous year
      </div>
    </div>

    <div class="rounded border border-gray-300 bg-white px-4 py-5 lg:w-full">
      <h3
        class="text-base font-semibold uppercase leading-8 tracking-normal text-gray-700 md:leading-3"
      >
        <span class="text-sm"> Scope 1 </span>
      </h3>
      <div class="mb-2 mt-2 h-0.5 w-full bg-veridis-scope-1" />
      <div class="mt-3 flex items-center no-localize-translate">
        <h4
          class="font-medium text-slate-900"
          :class="{ 'text-2xl': isTextOverflow, 'text-3xl': !isTextOverflow }"
        >
          {{ currentYearScopeOne }}
        </h4>
        <span class="ml-2 text-sm text-gray-400">tCO2e</span>
      </div>
      <div class="mt-2 whitespace-nowrap text-sm font-medium text-gray-600">
        <span
          :class="[
            previousYearIncreaseScopeOne === 0
              ? 'text-gray-400'
              : previousYearIncreaseScopeOne > 0
                ? 'text-red-500'
                : 'text-green-500',
          ]"
        >
          {{ getPercentageDisplay(previousYearIncreaseScopeOne) }}
        </span>
        from the previous year
      </div>
    </div>

    <div class="rounded border border-gray-300 bg-white px-4 py-5 lg:w-full">
      <h3
        class="text-base font-semibold uppercase leading-8 tracking-normal text-gray-700 md:leading-3"
      >
        <span class="text-sm"> Scope 2 </span>
      </h3>
      <div class="mb-2 mt-2 h-0.5 w-full bg-veridis-scope-2" />
      <div class="mt-3 flex items-center no-localize-translate">
        <h4
          class="font-medium text-slate-900"
          :class="{ 'text-2xl': isTextOverflow, 'text-3xl': !isTextOverflow }"
        >
          {{ currentYearScopeTwo }}
        </h4>
        <span class="ml-2 text-sm text-gray-400">tCO2e</span>
      </div>
      <div class="mt-2 whitespace-nowrap text-sm font-medium text-gray-600">
        <span
          :class="[
            previousYearIncreaseScopeTwo === 0
              ? 'text-gray-400'
              : previousYearIncreaseScopeTwo > 0
                ? 'text-red-500'
                : 'text-green-500',
          ]"
        >
          {{ getPercentageDisplay(previousYearIncreaseScopeTwo) }}
        </span>
        from the previous year
      </div>
    </div>

    <div class="rounded border border-gray-300 bg-white px-4 py-5 lg:w-full">
      <h3
        class="text-base font-semibold uppercase leading-8 tracking-normal text-gray-700 md:leading-3"
      >
        <span class="text-sm"> Scope 3 </span>
      </h3>
      <div class="mb-2 mt-2 h-0.5 w-full bg-veridis-scope-3" />
      <div class="mt-3 flex items-center no-localize-translate">
        <h4
          class="font-medium text-slate-900"
          :class="{ 'text-2xl': isTextOverflow, 'text-3xl': !isTextOverflow }"
        >
          {{ currentYearScopeThree }}
        </h4>
        <span class="ml-2 text-sm text-gray-400">tCO2e</span>
      </div>
      <div class="mt-2 whitespace-nowrap text-sm font-medium text-gray-600">
        <span
          :class="[
            previousYearIncreaseScopeThree === 0
              ? 'text-gray-400'
              : previousYearIncreaseScopeThree > 0
                ? 'text-red-500'
                : 'text-green-500',
          ]"
        >
          {{ getPercentageDisplay(previousYearIncreaseScopeThree) }}
        </span>
        from the previous year
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GhgEmissionCards',

  props: {
    currentYearReporting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      currentYearTotalEmissions: 0,
      currentYearScopeOne: 0,
      currentYearScopeTwo: 0,
      currentYearScopeThree: 0,
      previousYearIncrease: 0,
      previousYearIncreaseScopeOne: 0,
      previousYearIncreaseScopeTwo: 0,
      previousYearIncreaseScopeThree: 0,
    }
  },

  computed: {
    isTextOverflow() {
      return this.currentYearTotalEmissions.length > 10
    },
  },

  watch: {
    currentYearReporting(newReporting) {
      // Current Year Emissions as shown on the cards
      const currentYearTotalEmissionsData
        = newReporting.current_reporting_year_total_emissions.toFixed(2)
      this.currentYearTotalEmissions = thousands(
        currentYearTotalEmissionsData,
        2,
      )

      const currentYearScopeOneData
        = newReporting.current_reporting_year_scope_one_total_emissions.toFixed(
          2,
        )
      this.currentYearScopeOne = thousands(currentYearScopeOneData, 2)

      const currentYearScopeTwoData
        = newReporting.current_reporting_year_scope_two_total_emissions.toFixed(
          2,
        )
      this.currentYearScopeTwo = thousands(currentYearScopeTwoData, 2)

      const currentYearScopeThreeData
        = newReporting.current_reporting_year_scope_three_total_emissions.toFixed(
          2,
        )
      this.currentYearScopeThree = thousands(currentYearScopeThreeData, 2)

      // Previous Year Increase
      const previousYearIncreaseData
        = newReporting.total_emissions_percentage_increase.toFixed(2)
      this.previousYearIncrease = Number(previousYearIncreaseData)

      const previousYearIncreaseScopeOneData
        = newReporting.scope_one_emissions_percentage_increase.toFixed(2)
      this.previousYearIncreaseScopeOne = Number(
        previousYearIncreaseScopeOneData,
      )

      const previousYearIncreaseScopeTwoData
        = newReporting.scope_two_emissions_percentage_increase.toFixed(2)
      this.previousYearIncreaseScopeTwo = Number(
        previousYearIncreaseScopeTwoData,
      )

      const previousYearIncreaseScopeThreeData
        = newReporting.scope_three_emissions_percentage_increase.toFixed(2)
      this.previousYearIncreaseScopeThree = Number(
        previousYearIncreaseScopeThreeData,
      )
    },
  },

  methods: {
    formatThousands(value, decimal = 2) {
      return thousands(value, decimal)
    },
    getPercentageDisplay(increase) {
      return `${increase === 0 ? '--' : increase > 0 ? '+' : '-'}${increase !== 0 ? thousands(Math.abs(increase)) + '%' : ''}`
    },
  },
}
</script>
